import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DataTable from "../../Table/DataTable";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import { projectStatusLabel, projectStatusOptions } from "./projectStatuses";
import { fetchProjects } from "../../../REDUX/actions/appProjects";
import { getModels, isSpaModel } from "../../../REDUX/actions/model";
import { fetchCommercialOptions } from "../../../REDUX/actions/commercials";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import AddProjectModal from "./AddProjectModal";
import ProjectModal from "./ProjectModal";
import QuotePill from "./QuotePill";

const fullName = (user) =>
  `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
  user?.username ||
  "-";

// Filters (behind the DataTable "Filters" button): Status, Date de relance,
// Contact, Commercial, Modèle (produit).
const filterElements = [
  {
    type: "select",
    label: "Status",
    name: "status",
    placeholder: "Status",
    resetable: true,
    showClear: true,
  },
  {
    type: "date",
    label: "Reminder date",
    name: "relanceDate",
    placeholder: "Reminder date",
    minDate: false,
  },
  {
    type: "text",
    label: "Contact",
    name: "source",
    placeholder: "Contact",
  },
  {
    type: "select",
    label: "Commercial",
    name: "salesRep",
    placeholder: "Commercial",
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Model",
    name: "model",
    placeholder: "Model",
    filter: true,
    resetable: true,
    showClear: true,
  },
];

/** Slide-3 columns; actions come sorted createdAt desc from the backend. */
const ProjectsTable = ({ toConfirm = false }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const { projects, loading, pageCount, total, version } = useSelector(
    (state) => state.appProjectsReducer
  );
  const { models } = useSelector((state) => state.modelReducer) ?? {};
  const { scope } = useSelector((state) => state.appCustomersReducer);
  const [commercialOptions, setCommercialOptions] = useState([]);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    if (!models?.length) {
      dispatch(getModels(i18n.language?.includes("en") ? "en" : "fr"));
    }
    // The tab remounts on a scope change (store filter).
    fetchCommercialOptions(scope.organizationId, controller.signal)
      .then(setCommercialOptions)
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = (page, search, tableFilters, signal) => {
    dispatch(fetchProjects(page, search, tableFilters, signal));
  };

  // Stable identity — DataTable refetches on [filters] reference change, so a
  // fresh object every render would loop. version bump forces the refetch
  // after any project/action mutation.
  const tableFilters = useMemo(
    () => ({ ...filters, version, toConfirm }),
    [filters, version, toConfirm]
  );

  const selectOptions = {
    status: projectStatusOptions(t),
    model: (models ?? [])
      .filter(isSpaModel)
      .map(({ name, code }) => ({ name, code })),
    salesRep: commercialOptions,
  };

  const headers = [
    "Project number",
    "Name",
    "Contact",
    "Interested model",
    "Budget",
    "Status",
    "Last action",
    "Reminder date",
    "Commercial",
    "",
  ];
  const columns = [
    { name: "Project number", render: (row) => row.number ?? "-" },
    { name: "Name", render: (row) => row.name ?? "-" },
    { name: "Contact", render: (row) => fullName(row.mainContact) },
    {
      name: "Interested model",
      render: (row) =>
        (row.interestedModels ?? []).map((model) => model.name).join(", ") ||
        "-",
    },
    { name: "Budget", render: (row) => row.budget ?? "-" },
    {
      name: "Status",
      render: (row) =>
        // A quote accepted in the app waits for the advisor's order (N1).
        (row.quotes ?? []).some((q) => q.state === "accepted_by_customer") ? (
          <QuotePill tone="copper" dot="#c9762e">
            {t("Quote to confirm")}
          </QuotePill>
        ) : (
          projectStatusLabel(row.status, t)
        ),
    },
    {
      name: "Last action",
      render: (row) => {
        const last = (row.actions ?? []).find((a) => !a.isReminder);
        return last ? t(`${last.type ?? ""}`) : "-";
      },
    },
    {
      name: "Reminder date",
      render: (row) => {
        const reminder = (row.actions ?? []).find((a) => a.isReminder);
        return reminder?.date
          ? moment(reminder.date).format("DD/MM/YYYY")
          : "-";
      },
    },
    {
      name: "Commercial",
      // The project's commercial, else its advisor account.
      render: (row) => fullName(row.salesRep ?? row.commercial),
    },
    {
      name: "",
      render: (row) => (
        <VerticalPoints
          showLeft
          options={[
            {
              text: t("Edit"),
              callback: () =>
                dispatch(openRightModal(<ProjectModal projectId={row.id} />)),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      title="Project"
      total={total}
      showRowDetails={false}
      loading={loading}
      headers={headers}
      columns={columns}
      data={projects}
      totalPages={pageCount}
      fetchData={fetchData}
      filters={tableFilters}
      setFilters={setFilters}
      filterElements={filterElements}
      selectOptions={selectOptions}
      addNewItem={() => dispatch(openCenteredModal(<AddProjectModal />))}
      newItemTitle={t("Add a project")}
      noResultsMessage={t("No projects found.")}
      containerClassName="pt-2"
    />
  );
};

export default ProjectsTable;
